import { useMemo, useState } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SERVICES } from "@/content/navigation";
import { api, formatApiError, hasBackend } from "@/lib/api";

const field = "h-12 rounded-md border-ink/15 bg-white text-[16px] text-ink placeholder:text-ink/50 focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-0";

const Field = ({ id, label, required, children }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="text-[12px] font-semibold uppercase tracking-[0.14em] text-ink/70">
      {label} {required && <span className="text-plum-2">*</span>}
    </Label>
    {children}
  </div>
);

const Success = ({ reset }) => (
  <div className="flex flex-col items-start py-10" data-testid="enquiry-success">
    <CheckCircle2 className="h-12 w-12 text-plum-2" strokeWidth={1.3} />
    <h3 className="mt-6 font-display text-3xl font-light text-ink">Thank you — your enquiry has been received.</h3>
    <p className="mt-4 max-w-md text-ink/70">A member of the VION Tech team will review your message and get back to you.</p>
    <button type="button" onClick={reset} className="mt-8 min-h-[44px] text-[12px] font-semibold uppercase tracking-[0.16em] text-plum underline-offset-4 hover:underline" data-testid="enquiry-send-another">
      Send another enquiry
    </button>
  </div>
);

export const EnquiryForm = () => {
  const [params] = useSearchParams();
  const { pathname } = useLocation();
  const initial = useMemo(() => {
    const svc = params.get("service");
    const topic = params.get("topic");
    return {
      name: "", email: "", phone: "", organisation: "", website: "",
      service: SERVICES.includes(svc) ? svc : "",
      message: topic ? `I'd like to learn more about ${topic}.` : "",
    };
  }, [params]);
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.service) { setError("Please choose a service of interest."); return; }
    if (!hasBackend) { setError("Enquiries are temporarily unavailable. Please try again later."); return; }
    setStatus("loading");
    try {
      await api.post("/enquiries", { ...form, source_page: pathname });
      setStatus("done");
      toast.success("Enquiry sent. Thank you!");
    } catch (err) {
      setStatus("idle");
      setError(formatApiError(err));
    }
  };

  if (status === "done") return <Success reset={() => { setForm({ ...initial, message: "" }); setStatus("idle"); }} />;

  return (
    <form onSubmit={submit} className="grid gap-6 sm:grid-cols-2" data-testid="enquiry-form" noValidate={false}>
      <Field id="name" label="Full Name" required>
        <Input id="name" required minLength={2} autoComplete="name" value={form.name} onChange={set("name")} className={field} placeholder="Your full name" data-testid="enquiry-name-input" />
      </Field>
      <Field id="email" label="Email" required>
        <Input id="email" type="email" required autoComplete="email" value={form.email} onChange={set("email")} className={field} placeholder="you@example.com" data-testid="enquiry-email-input" />
      </Field>
      <Field id="phone" label="Phone / WhatsApp">
        <Input id="phone" type="tel" autoComplete="tel" value={form.phone} onChange={set("phone")} className={field} placeholder="+234 …" data-testid="enquiry-phone-input" />
      </Field>
      <Field id="organisation" label="Organisation">
        <Input id="organisation" autoComplete="organization" value={form.organisation} onChange={set("organisation")} className={field} placeholder="Company, school or institution" data-testid="enquiry-organisation-input" />
      </Field>
      <div className="sm:col-span-2">
        <Field id="service" label="Service of Interest" required>
          <Select value={form.service} onValueChange={set("service")}>
            <SelectTrigger id="service" className={field} data-testid="enquiry-service-select">
              <SelectValue placeholder="Select a service" />
            </SelectTrigger>
            <SelectContent data-lenis-prevent>
              {SERVICES.map((s) => (
                <SelectItem key={s} value={s} className="min-h-[44px] text-[15px]" data-testid={`enquiry-service-option-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>
      <div className="sm:col-span-2">
        <Field id="message" label="Message" required>
          <Textarea id="message" required minLength={5} rows={6} value={form.message} onChange={set("message")} className={`${field} h-auto min-h-[160px] py-3`} placeholder="Tell us a little about what you're looking for" data-testid="enquiry-message-input" />
        </Field>
      </div>
      <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} className="hidden" aria-hidden="true" />
      {error && <p className="text-sm text-red-700 sm:col-span-2" role="alert" data-testid="enquiry-error">{error}</p>}
      <div className="flex flex-col gap-4 sm:col-span-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-[13px] text-ink/60">We'll only use your details to respond to your enquiry.</p>
        <button
          type="submit"
          disabled={status === "loading"}
          className="inline-flex min-h-[52px] items-center justify-center gap-2 rounded-full bg-plum px-8 text-[13px] font-semibold uppercase tracking-[0.16em] text-white transition-[background-color,transform] duration-300 hover:bg-plum-2 active:scale-[0.98] disabled:opacity-60"
          data-testid="enquiry-submit-btn"
        >
          {status === "loading" && <Loader2 className="h-4 w-4 animate-spin" />}
          Send Enquiry
        </button>
      </div>
    </form>
  );
};
